import { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import lazyWithRetry from './utils/lazyWithRetry';

// Lazy-loaded page components for code-splitting with retry support
// This handles stale chunk errors after deployments by auto-reloading the page
const Frontdesk = lazyWithRetry(() => import('./pages/Frontdesk/Frontdesk'), 'Frontdesk');
const DesignSystem = lazyWithRetry(() => import('./pages/DesignSystem/DesignSystem'), 'DesignSystem');
const DevApiTester = lazyWithRetry(() => import('./pages/DevApiTester/DevApiTester'), 'DevApiTester');
const DevWhatsAppSync = lazyWithRetry(() => import('./pages/DevWhatsAppSync/DevWhatsAppSync'), 'DevWhatsAppSync');
const WhatsAppInbox = lazyWithRetry(() => import('./pages/WhatsAppInbox/WhatsAppInbox'), 'WhatsAppInbox');
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard/Dashboard'), 'Dashboard');
const OEMValidation = lazyWithRetry(() => import('./pages/OEMValidation/OEMValidation'), 'OEMValidation');
const PartsV2 = lazyWithRetry(() => import('./pages/Parts/PartsV2'), 'PartsV2');
const VehiclesV2 = lazyWithRetry(() => import('./pages/Vehicles/VehiclesV2'), 'VehiclesV2');
const SalesReports = lazyWithRetry(() => import('./pages/SalesReports/SalesReports'), 'SalesReports');
const ReturnsReport = lazyWithRetry(() => import('./pages/ReturnsReport/ReturnsReport'), 'ReturnsReport');
const ReportsDashboard = lazyWithRetry(() => import('./pages/Reports/ReportsDashboard'), 'ReportsDashboard');
const ResearchDashboard = lazyWithRetry(() => import('./pages/Reports/ResearchDashboard'), 'ResearchDashboard');
const AddedPartsReport = lazyWithRetry(() => import('./pages/Reports/AddedPartsReport'), 'AddedPartsReport');
const UserProductActivityReport = lazyWithRetry(() => import('./pages/Reports/UserProductActivityReport'), 'UserProductActivityReport');
const QuoteMissedCallsReport = lazyWithRetry(() => import('./pages/Reports/QuoteMissedCallsReport'), 'QuoteMissedCallsReport');
const CombinedDailyPerformanceReport = lazyWithRetry(() => import('./pages/Reports/CombinedDailyPerformanceReport'), 'CombinedDailyPerformanceReport');
const CombinedUserPerformanceReport = lazyWithRetry(() => import('./pages/Reports/CombinedUserPerformanceReport'), 'CombinedUserPerformanceReport');
const CallCenterDailyPerformanceReport = lazyWithRetry(() => import('./pages/Reports/CallCenterDailyPerformanceReport'), 'CallCenterDailyPerformanceReport');
const Settings = lazyWithRetry(() => import('./pages/Settings/Settings'), 'Settings');
const UserSettings = lazyWithRetry(() => import('./pages/Settings/UserSettings'), 'UserSettings');
const VehicleModels = lazyWithRetry(() => import('./pages/Settings/VehicleModelsList'), 'VehicleModels');
const AddVehicleModel = lazyWithRetry(() => import('./pages/Settings/AddVehicleModel'), 'AddVehicleModel');
const AgentNeedsList = lazyWithRetry(() => import('./pages/Settings/AgentNeedsList'), 'AgentNeedsList');
const Tasks = lazyWithRetry(() => import('./pages/Tasks/Tasks'), 'Tasks');
const CreateQuote = lazyWithRetry(() => import('./pages/CRM/Quotes').then(m => ({ default: m.CreateQuote })), 'CreateQuote');
const CreateVehicleSale = lazyWithRetry(() => import('./pages/CRM/Quotes/CreateVehicleSale'), 'CreateVehicleSale');
const CreateKranQuote = lazyWithRetry(() => import('./pages/CRM/Quotes/CreateKranQuote'), 'CreateKranQuote');
const KranQuoteDetail = lazyWithRetry(() => import('./pages/CRM/Quotes/KranQuoteDetail'), 'KranQuoteDetail');
const KranQuotesList = lazyWithRetry(() => import('./pages/CRM/Quotes/KranQuotesList.jsx'), 'KranQuotesList');
const KranContractsList = lazyWithRetry(() => import('./pages/CRM/KranContracts/KranContractsList.jsx'), 'KranContractsList');
const KranContractsV2 = lazyWithRetry(() => import('./pages/CRM/KranContracts/KranContractsV2.jsx'), 'KranContractsV2');
const KranContractsV3 = lazyWithRetry(() => import('./pages/CRM/KranContracts/KranContractsV3.jsx'), 'KranContractsV3');
const KranDevizeList = lazyWithRetry(() => import('./pages/CRM/KranDevize/KranDevizeList.jsx'), 'KranDevizeList');
const KranDevizDetail = lazyWithRetry(() => import('./pages/CRM/KranDevize/KranDevizDetail.jsx'), 'KranDevizDetail');
const KranTransportNoticesList = lazyWithRetry(() => import('./pages/CRM/KranTransportNotices/KranTransportNoticesList.jsx'), 'KranTransportNoticesList');
const KranTendersList = lazyWithRetry(() => import('./pages/CRM/KranTenders/KranTendersList.jsx'), 'KranTendersList');
const KranContractDetail = lazyWithRetry(() => import('./pages/CRM/KranContracts/KranContractDetail.jsx'), 'KranContractDetail');
const KranWorkOrdersList = lazyWithRetry(() => import('./pages/CRM/KranWorkOrders/KranWorkOrdersList.jsx'), 'KranWorkOrdersList');
const KranWorkOrderDetail = lazyWithRetry(() => import('./pages/CRM/KranWorkOrders/KranWorkOrderDetail.jsx'), 'KranWorkOrderDetail');
const KranPartsList = lazyWithRetry(() => import('./pages/CRM/KranParts/KranPartsList.jsx'), 'KranPartsList');
const CreateKranPart = lazyWithRetry(() => import('./pages/CRM/KranParts/CreateKranPart.jsx'), 'CreateKranPart');
const QuotesList = lazyWithRetry(() => import('./pages/CRM/Quotes/QuotesList.jsx'), 'QuotesList');
const SalesList = lazyWithRetry(() => import('./pages/CRM/Sales').then(m => ({ default: m.SalesList })), 'SalesList');
const PaymentsList = lazyWithRetry(() => import('./pages/CRM/Payments').then(m => ({ default: m.PaymentsList })), 'PaymentsList');
const ReconciliationDocumentsList = lazyWithRetry(() => import('./pages/CRM/Payments').then(m => ({ default: m.ReconciliationDocumentsList })), 'ReconciliationDocumentsList');
const ReconciliationDocumentDetail = lazyWithRetry(() => import('./pages/CRM/Payments').then(m => ({ default: m.ReconciliationDocumentDetail })), 'ReconciliationDocumentDetail');
const InvoicesList = lazyWithRetry(() => import('./pages/CRM/Invoices').then(m => ({ default: m.InvoicesList })), 'InvoicesList');
const InvoiceDetail = lazyWithRetry(() => import('./pages/CRM/Invoices').then(m => ({ default: m.InvoiceDetail })), 'InvoiceDetail');
const VsuList = lazyWithRetry(() => import('./pages/CRM/Vsu').then(m => ({ default: m.VsuList })), 'VsuList');
const CashRegisterPage = lazyWithRetry(() => import('./pages/CRM/CashRegister').then(m => ({ default: m.CashRegisterPage })), 'CashRegisterPage');
const AccountingReportsPage = lazyWithRetry(() => import('./pages/CRM/AccountingReports').then(m => ({ default: m.AccountingReportsPage })), 'AccountingReportsPage');
const WasteExitsPage = lazyWithRetry(() => import('./pages/CRM/WasteExits').then(m => ({ default: m.WasteExitsPage })), 'WasteExitsPage');
const TransportOrdersPage = lazyWithRetry(() => import('./pages/CRM/TransportOrders').then(m => ({ default: m.TransportOrdersPage })), 'TransportOrdersPage');
const ExpenseReceiptsList = lazyWithRetry(() => import('./pages/CRM/ExpenseReceipts').then(m => ({ default: m.ExpenseReceiptsList })), 'ExpenseReceiptsList');
const VsuDetail = lazyWithRetry(() => import('./pages/CRM/Vsu').then(m => ({ default: m.VsuDetail })), 'VsuDetail');
const BankStatementsList = lazyWithRetry(() => import('./pages/CRM/BankStatements').then(m => ({ default: m.BankStatementsList })), 'BankStatementsList');
const PurchaseInvoicesList = lazyWithRetry(() => import('./pages/CRM/PurchaseInvoices').then(m => ({ default: m.PurchaseInvoicesList })), 'PurchaseInvoicesList');
const PurchaseInvoiceDetail = lazyWithRetry(() => import('./pages/CRM/PurchaseInvoices').then(m => ({ default: m.PurchaseInvoiceDetail })), 'PurchaseInvoiceDetail');
const InHouseWorkReportsList = lazyWithRetry(() => import('./pages/CRM/InHouseWorkReports').then(m => ({ default: m.InHouseWorkReportsList })), 'InHouseWorkReportsList');
const InHouseWorkReportDetail = lazyWithRetry(() => import('./pages/CRM/InHouseWorkReports').then(m => ({ default: m.InHouseWorkReportDetail })), 'InHouseWorkReportDetail');
const ReturnsList = lazyWithRetry(() => import('./pages/CRM/Returns/ReturnsList'), 'ReturnsList');
const EmployeesList = lazyWithRetry(() => import('./pages/CRM/Employees/EmployeesList.jsx'), 'EmployeesList');
const TimesheetsPage = lazyWithRetry(() => import('./pages/CRM/Timesheets').then(m => ({ default: m.TimesheetsPage })), 'TimesheetsPage');
const LeavesPage = lazyWithRetry(() => import('./pages/CRM/Timesheets').then(m => ({ default: m.LeavesPage })), 'LeavesPage');
const UsersList = lazyWithRetry(() => import('./pages/CRM/Users/UsersList.jsx'), 'UsersList');
const EmployeeDocumentsList = lazyWithRetry(() => import('./pages/CRM/EmployeeDocuments/EmployeeDocumentsList.jsx'), 'EmployeeDocumentsList');
const FleetList = lazyWithRetry(() => import('./pages/CRM/Fleet/FleetList.jsx'), 'FleetList');
const FleetDetail = lazyWithRetry(() => import('./pages/CRM/Fleet/FleetDetail.jsx'), 'FleetDetail');
const FleetDocumentsList = lazyWithRetry(() => import('./pages/CRM/Fleet/FleetDocumentsList.jsx'), 'FleetDocumentsList');
const WorkOrdersList = lazyWithRetry(() => import('./pages/CRM/Fleet/WorkOrdersList.jsx'), 'WorkOrdersList');
const TripSheets = lazyWithRetry(() => import('./pages/CRM/Fleet/TripSheets.jsx'), 'TripSheets');
const FuelLedger = lazyWithRetry(() => import('./pages/CRM/Fleet/FuelLedger.jsx'), 'FuelLedger');
const CadastralLookupPage = lazyWithRetry(() => import('./pages/Agriculture/CadastralLookupPage.jsx'), 'CadastralLookupPage');
const LeaseList = lazyWithRetry(() => import('./pages/Agriculture/LeaseList.jsx'), 'LeaseList');
const LeaseContractsPage = lazyWithRetry(() => import('./pages/Agriculture/LeaseContractsPage.jsx'), 'LeaseContractsPage');
const LeaseDetail = lazyWithRetry(() => import('./pages/Agriculture/LeaseDetail.jsx'), 'LeaseDetail');
const LeasePaymentsList = lazyWithRetry(() => import('./pages/Agriculture/LeasePaymentsList.jsx'), 'LeasePaymentsList');
const LandParcelsList = lazyWithRetry(() => import('./pages/Agriculture/LandParcelsList.jsx'), 'LandParcelsList');
const CropDiversificationPage = lazyWithRetry(() => import('./pages/Agriculture/CropDiversificationPage.jsx'), 'CropDiversificationPage');
const ParcelView = lazyWithRetry(() => import('./pages/Parcels/ParcelView.jsx'), 'ParcelView');
const InventoryJobsList = lazyWithRetry(() => import('./pages/CRM/Inventory/InventoryJobsList.jsx'), 'InventoryJobsList');
const InventoryJobDetail = lazyWithRetry(() => import('./pages/CRM/Inventory/InventoryJobDetail.jsx'), 'InventoryJobDetail');
const LineItemsList = lazyWithRetry(() => import('./pages/CRM/LineItems/LineItemsList.jsx'), 'LineItemsList');
const LineItemDetail = lazyWithRetry(() => import('./pages/CRM/LineItems/LineItemDetail.jsx'), 'LineItemDetail');
const ReturnDetail = lazyWithRetry(() => import('./pages/CRM/Returns/ReturnDetail'), 'ReturnDetail');
const Research = lazyWithRetry(() => import('./pages/Research/Research'), 'Research');
const Login = lazyWithRetry(() => import('./pages/Auth/Login'), 'Login');
const CRM = lazyWithRetry(() => import('./pages/CRM'), 'CRM');
const CRMTasksList = lazyWithRetry(() => import('./pages/CRMTasks').then(m => ({ default: m.CRMTasksList })), 'CRMTasksList');
const Picking = lazyWithRetry(() => import('./pages/CRMTasks/Picking'), 'Picking');
const Packing = lazyWithRetry(() => import('./pages/CRMTasks/Packing'), 'Packing');
const Printare = lazyWithRetry(() => import('./pages/CRMTasks/Printare'), 'Printare');
const ClientRequests = lazyWithRetry(() => import('./pages/ClientRequests/ClientRequests'), 'ClientRequests');
const KranQuotes = lazyWithRetry(() => import('./pages/KranQuotes/KranQuotes'), 'KranQuotes');
const AddVehicle = lazyWithRetry(() => import('./pages/Vehicles/AddVehicle'), 'AddVehicle');
const Notifications = lazyWithRetry(() => import('./pages/Notifications'), 'Notifications');
const ShipmentLocations = lazyWithRetry(() => import('./pages/Settings/ShipmentLocations'), 'ShipmentLocations');
const ShipmentUnmappedCities = lazyWithRetry(() => import('./pages/Settings/ShipmentUnmappedCities'), 'ShipmentUnmappedCities');
const ComponentsShipping = lazyWithRetry(() => import('./pages/Settings/ComponentsShipping'), 'ComponentsShipping');
const ComponentsVerification = lazyWithRetry(() => import('./pages/Settings/ComponentsVerification'), 'ComponentsVerification');
const PenaltyCalculator = lazyWithRetry(() => import('./pages/Settings/PenaltyCalculator'), 'PenaltyCalculator');
const LineItemsTest = lazyWithRetry(() => import('./pages/LineItemsTest/LineItemsTest'), 'LineItemsTest');
const TestPage = lazyWithRetry(() => import('./pages/TestPage/TestPage'), 'TestPage');
const AgentSessions = lazyWithRetry(() => import('./pages/AgentSessions/AgentSessions'), 'AgentSessions');
const KioskStart = lazyWithRetry(() => import('./pages/Kiosk/KioskStart'), 'KioskStart');
const KioskAuthorize = lazyWithRetry(() => import('./pages/Kiosk/KioskAuthorize'), 'KioskAuthorize');
// CRM Quotes components (named exports) lazy-loaded above already
import AuthProvider from './providers/AuthProvider';
import { useAuth } from './providers/auth-context';
import KioskProvider from './providers/KioskProvider';
import { isKioskMode, isPackingTerminal } from './services/api/kiosk.service';
import { useKiosk } from './providers/kiosk-context';
const PackingTerminal = lazyWithRetry(() => import('./pages/Kiosk/PackingTerminal'), 'PackingTerminal');
import QueryProvider from './providers/QueryProvider';
import DragDropProvider from './providers/DragDropProvider';
import WhatsAppDrawerProvider from './providers/WhatsAppDrawerProvider';
import { RecordingPlayerProvider } from './providers/recording-player-context';
import RecordingPlayerCard from './components/common/CallController/RecordingPlayerCard';
import webSocketService from './services/websocket.service';
import { authService } from './services/api';
import { useTranscodeUpdates } from './hooks/useTranscodeUpdates';
import { useRecordChangeListInvalidation } from './hooks/useRecordChanges';
import { useWhatsAppUnreadInvalidation } from './hooks/useWhatsAppUnread';
import { useWhatsAppBadgeFeeds } from './hooks/useWhatsAppBadgeFeeds';
import { useTypingPresence } from './hooks/useTypingPresence';
import { TooltipProvider } from '@/components/ui/tooltip';

import LoadingSpinner from './components/common/LoadingSpinner/LoadingSpinner';
import './styles/global.css';
import PropTypes from 'prop-types';
// import API_CONFIG from './config/api.config';
// import API_CONFIG from './config/api.config';


// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center">
          <LoadingSpinner size="lg" color="primary" className="mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

// Permission Protected Route Component
const PermissionProtectedRoute = ({ children, requiredPermission }) => {
  const { isAuthenticated, isLoading, hasPermission } = useAuth();
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center">
          <LoadingSpinner size="lg" color="primary" className="mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!hasPermission(requiredPermission)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

PermissionProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requiredPermission: PropTypes.string.isRequired,
};

// Role Protected Route Component
const RoleProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, isLoading, hasRole } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center">
          <LoadingSpinner size="lg" color="primary" className="mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const isAllowed = Array.isArray(allowedRoles) && allowedRoles.some((r) => hasRole(r));
  if (!isAllowed) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

RoleProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  allowedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
};

// Development Routes Component
/** A moved page: the old address keeps working, with its filters. */
const RedirectKeepingQuery = ({ to }) => {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
};
RedirectKeepingQuery.propTypes = { to: PropTypes.string.isRequired };

const DevRoutes = () => (
  <Routes>
    <Route path="/design" element={<DesignSystem />} />
  </Routes>
);

function AppRoutes() {
  const location = useLocation();
  const kiosk = useKiosk();
  const packingTerminal = kiosk.terminal?.terminal_type === 'packing' || isPackingTerminal();
  const { isAuthenticated, isLoading, user } = useAuth();
  const isDevelopment = import.meta.env.DEV;

  // Surgically patch transcode_update WS pushes into the query cache (no refetch).
  useTranscodeUpdates();
  // Keep CRM list pages + status counts live on record_change WS pushes; also
  // flags foreign-user changes so own-edit suppression can't swallow them.
  useRecordChangeListInvalidation(user?.userId);
  // Ephemeral "someone is typing a comment" presence (record_typing WS): one
  // app-wide subscription + expiry sweep, feeding the per-line-item dots.
  useTypingPresence();
  // Keep the unread badge on every WhatsApp phone icon live on whatsapp_update
  // WS pushes (one shared query key, one subscription for the whole app).
  useWhatsAppUnreadInvalidation();
  useWhatsAppBadgeFeeds({ enabled: isAuthenticated && !packingTerminal });

  // Initialize WebSocket when authenticated
  useEffect(() => {
    if (isAuthenticated && user?.userId != null && !packingTerminal) {
      // A getter, not a value: this effect only re-runs on session changes, so
      // a captured string stays the token minted at login and is refused by the
      // server 15 minutes later (60s on a kiosk) — every reconnect after that
      // would handshake with a dead credential. Refresh first when it is past
      // its TTL, so a tab that slept through the expiry comes back on its own.
      webSocketService.connect(async () => {
        if (authService.isTokenExpired()) {
          return await authService.refreshAccessToken();
        }
        return authService.getAccessToken();
      }, user.userId);
    }
    // Cleanup on unmount
    return () => {
      webSocketService.disconnect();
    };
  }, [isAuthenticated, packingTerminal, user?.userId]);

  if (isLoading) {
    return null; // or a loading spinner
  }

  if (location.pathname === '/kiosk/start') {
    return <Suspense fallback={null}><KioskStart /></Suspense>;
  }

  if (packingTerminal) {
    return <Suspense fallback={<div className="min-h-screen bg-white" />}><PackingTerminal /></Suspense>;
  }

  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center">
          <LoadingSpinner size="lg" color="primary" className="mx-auto" />
          <p className="mt-4 text-gray-600">Loading page...</p>
        </div>
      </div>
    }>
      <Routes>
        {/* Public Routes */}
        {/* On an enrolled public terminal the password form is never rendered —
            not hidden with CSS, simply not mounted. The QR lock overlay is the
            only way in there, and the backend refuses /auth/v2/login from a
            kiosk device anyway (see docs/kiosk-qr-auth.md). */}
        <Route path="/login" element={
          isKioskMode() ? null : (
            !isAuthenticated ? <Login /> : <Navigate to="/dashboard" replace />
          )
        } />

        {/* Kiosk: terminal enrollment (launched by start-kiosk.bat) and the
            one-tap authorize page opened by scanning the terminal's QR. */}
        <Route path="/kiosk/start" element={<KioskStart />} />
        <Route path="/p/:code" element={<KioskAuthorize />} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />

        <Route path="/parts" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","call_center","orders","field_agent","listing","reception","researcher","testers"]}>
            <PartsV2 />
          </RoleProtectedRoute>
        } />

        <Route path="/vehicles" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","call_center","orders","field_agent","listing","reception","researcher","testers"]}>
            <VehiclesV2 />
          </RoleProtectedRoute>
        } />

        <Route path="/vehicles/new" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","call_center","orders","field_agent","listing","reception","researcher","testers"]}>
            <AddVehicle />
          </RoleProtectedRoute>
        } />

        {/* Achiziții VSU: the cars bought for dismantling, their papers and documents. */}
        <Route path="/vsu" element={
          <RoleProtectedRoute allowedRoles={['owner','admin','finance_manager','reception','call_center','orders','listing']}>
            <VsuList />
          </RoleProtectedRoute>
        } />
        <Route path="/vsu/:vehicleHash" element={
          <RoleProtectedRoute allowedRoles={['owner','admin','finance_manager','reception','call_center','orders','listing']}>
            <VsuDetail />
          </RoleProtectedRoute>
        } />

        <Route path="/frontdesk" element={
          <ProtectedRoute>
            <Frontdesk />
          </ProtectedRoute>
        } />

        <Route path="/research" element={
          <ProtectedRoute>
            <Research />
          </ProtectedRoute>
        } />

        <Route path="/research-dashboard" element={
          <RoleProtectedRoute allowedRoles={['owner', 'admin', 'researcher', 'ai_agent']}>
            <ResearchDashboard />
          </RoleProtectedRoute>
        } />

        <Route path="/reports" element={
          <ProtectedRoute permission='view_sales_reports'>
            <ReportsDashboard />
          </ProtectedRoute>
        } />

        <Route path="/sales-reports" element={
          <PermissionProtectedRoute requiredPermission="view_sales_reports">
            <SalesReports />
          </PermissionProtectedRoute>
        } />

        <Route path="/returns-report" element={
          <PermissionProtectedRoute requiredPermission="view_sales_reports">
            <ReturnsReport />
          </PermissionProtectedRoute>
        } />

        <Route path="/reports/added-parts" element={
          <PermissionProtectedRoute requiredPermission="view_sales_reports">
            <AddedPartsReport />
          </PermissionProtectedRoute>
        } />

        <Route path="/reports/user-product-activities" element={
          <PermissionProtectedRoute requiredPermission="view_sales_reports">
            <UserProductActivityReport />
          </PermissionProtectedRoute>
        } />

        <Route path="/reports/quote-missed-calls" element={
          <PermissionProtectedRoute requiredPermission="view_sales_reports">
            <QuoteMissedCallsReport />
          </PermissionProtectedRoute>
        } />
        <Route path="/reports/combined-daily" element={
          <PermissionProtectedRoute requiredPermission="view_sales_reports">
            <CombinedDailyPerformanceReport />
          </PermissionProtectedRoute>
        } />
        <Route path="/reports/callcenter-daily" element={
          <PermissionProtectedRoute requiredPermission="view_sales_reports">
            <CallCenterDailyPerformanceReport />
          </PermissionProtectedRoute>
        } />
        <Route path="/reports/combined-user" element={
          <PermissionProtectedRoute requiredPermission="view_sales_reports">
            <CombinedUserPerformanceReport />
          </PermissionProtectedRoute>
        } />

        <Route path="/oem-validation" element={
          <ProtectedRoute>
            <OEMValidation />
          </ProtectedRoute>
        } />

        <Route path="/tasks" element={
          <ProtectedRoute>
            <Tasks />
          </ProtectedRoute>
        } />

        <Route path="/quotes" element={
          <ProtectedRoute>
            <QuotesList />
          </ProtectedRoute>
        } />

        <Route path="/quotes/create" element={
          <ProtectedRoute>
            <CreateQuote />
          </ProtectedRoute>
        } />

        <Route path="/quotes/create-vehicle-sale" element={
          <ProtectedRoute>
            <CreateVehicleSale />
          </ProtectedRoute>
        } />

        <Route path="/quotes/create-kran" element={
          <ProtectedRoute>
            <CreateKranQuote />
          </ProtectedRoute>
        } />

        <Route path="/crm/kran-quotes" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","kran-admin"]}>
            <KranQuotesList />
          </RoleProtectedRoute>
        } />
        <Route path="/kran/quotes" element={<Navigate to="/crm/kran-quotes" replace />} />

        <Route path="/crm/kran-contracts" element={<Navigate to="/crm/kran-contracts-v3" replace />} />
        <Route path="/crm/kran-contracts-v2" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","kran-admin"]}>
            <KranContractsV2 />
          </RoleProtectedRoute>
        } />
        <Route path="/crm/kran-contracts-v3" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","kran-admin"]}>
            <KranContractsV3 />
          </RoleProtectedRoute>
        } />
        <Route path="/crm/kran-estimates" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","kran-admin"]}>
            <KranDevizeList />
          </RoleProtectedRoute>
        } />
        <Route path="/crm/kran-estimates/:id" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","kran-admin"]}>
            <KranDevizDetail />
          </RoleProtectedRoute>
        } />
        <Route path="/crm/kran-tenders" element={
          <RoleProtectedRoute allowedRoles={['owner']}>
            <KranTendersList />
          </RoleProtectedRoute>
        } />
        <Route path="/crm/kran-transport-notices" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","kran-admin"]}>
            <KranTransportNoticesList />
          </RoleProtectedRoute>
        } />
        <Route path="/crm/kran-contracts/:id" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","kran-admin","kran-operator"]}>
            <KranContractDetail />
          </RoleProtectedRoute>
        } />
        <Route path="/kran/contracts" element={<Navigate to="/crm/kran-contracts-v3" replace />} />

        <Route path="/crm/kran-work-orders" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","kran-admin","kran_manager","kran-operator"]}>
            <KranWorkOrdersList />
          </RoleProtectedRoute>
        } />
        <Route path="/crm/kran-work-orders/:id" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","kran-admin","kran_manager","kran-operator"]}>
            <KranWorkOrderDetail />
          </RoleProtectedRoute>
        } />

        <Route path="/crm/kran-parts" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","kran-admin","listing"]}>
            <KranPartsList />
          </RoleProtectedRoute>
        } />
        <Route path="/kran/parts" element={<Navigate to="/crm/kran-parts" replace />} />

        <Route path="/crm/kran-parts/new" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","kran-admin","listing"]}>
            <CreateKranPart />
          </RoleProtectedRoute>
        } />

        {/* CRM Sales new canonical path */}
        <Route path="/crm/sales" element={
          <ProtectedRoute>
            <SalesList />
          </ProtectedRoute>
        } />
        {/* Backward compatibility redirect */}
        <Route path="/sales" element={<Navigate to="/crm/sales" replace />} />

        <Route path="/crm/payments" element={
          <RoleProtectedRoute allowedRoles={['owner', 'finance_manager']}>
            <PaymentsList />
          </RoleProtectedRoute>
        } />
        <Route path="/crm/payments/documents" element={
          <RoleProtectedRoute allowedRoles={['owner', 'finance_manager']}>
            <ReconciliationDocumentsList />
          </RoleProtectedRoute>
        } />
        <Route path="/crm/payments/documents/:id" element={
          <RoleProtectedRoute allowedRoles={['owner', 'finance_manager']}>
            <ReconciliationDocumentDetail />
          </RoleProtectedRoute>
        } />
        {/* Extrase bancare: the same statements as the reconciliation documents,
            one row per account and day, only to find and open them. */}
        <Route path="/crm/bank-statements" element={
          <RoleProtectedRoute allowedRoles={['owner', 'finance_manager']}>
            <BankStatementsList />
          </RoleProtectedRoute>
        } />

        {/* Invoice register. The LIST is a finance surface like /crm/payments,
            plus kran-admin (the KRAN contract invoices tab reads from it) and
            call center / reception, who look up customers' invoices;
            the DETAIL is not role-gated, because anyone who can open a sale can
            already see its invoices tab and the search bar links straight here. */}
        <Route path="/crm/invoices" element={
          <RoleProtectedRoute allowedRoles={['owner', 'finance_manager', 'kran-admin', 'call_center', 'reception']}>
            <InvoicesList />
          </RoleProtectedRoute>
        } />
        <Route path="/crm/invoices/:invoiceId" element={
          <ProtectedRoute>
            <InvoiceDetail />
          </ProtectedRoute>
        } />
        {/* Registru de casă (the cash book): the counter keeps it (reception), the office checks it; agro: the farms' */}
        <Route path="/crm/cash-book" element={
          <RoleProtectedRoute allowedRoles={['owner', 'admin', 'finance_manager', 'reception', 'agro']}>
            <CashRegisterPage />
          </RoleProtectedRoute>
        } />
        {/* its first address: in English a cash register is the device (Casa de marcat) */}
        <Route path="/crm/cash-register" element={<RedirectKeepingQuery to="/crm/cash-book" />} />
        {/* Casa de marcat is part of the Registru de casă page (Raport X, Închide ziua) */}
        <Route path="/crm/fiscal-register" element={<RedirectKeepingQuery to="/crm/cash-book" />} />
        {/* Situații contabile: the monthly documents for the accountant, owner only for now. */}
        <Route path="/crm/accounting-reports" element={
          <RoleProtectedRoute allowedRoles={['owner']}>
            <AccountingReportsPage />
          </RoleProtectedRoute>
        } />
        {/* Ieșiri deșeuri: the register of waste leaving each firm, its avize and HG 1061 forms, the invoice made from it. */}
        <Route path="/crm/waste-exits" element={
          <RoleProtectedRoute allowedRoles={['owner', 'finance_manager']}>
            <WasteExitsPage />
          </RoleProtectedRoute>
        } />
        {/* Comenzi transport: the contract-comandă given to a carrier (CT series), replacing the "Contract – Comanda" sheet. */}
        <Route path="/crm/transport-orders" element={
          <RoleProtectedRoute allowedRoles={['owner', 'admin', 'finance_manager', 'reception', 'orders']}>
            <TransportOrdersPage />
          </RoleProtectedRoute>
        } />
        {/* Bonuri de cheltuială: cash (also in the register) and company card. */}
        <Route path="/crm/expense-receipts" element={
          <RoleProtectedRoute allowedRoles={['owner', 'admin', 'finance_manager', 'reception', 'agro']}>
            <ExpenseReceiptsList />
          </RoleProtectedRoute>
        } />
        <Route path="/crm/purchase-invoices" element={
          <RoleProtectedRoute allowedRoles={['owner', 'finance_manager']}>
            <PurchaseInvoicesList />
          </RoleProtectedRoute>
        } />
        <Route path="/crm/purchase-invoices/:purchaseInvoiceId" element={
          <RoleProtectedRoute allowedRoles={['owner', 'finance_manager']}>
            <PurchaseInvoiceDetail />
          </RoleProtectedRoute>
        } />
        <Route path="/crm/in-house-work-reports" element={
          <RoleProtectedRoute allowedRoles={['owner', 'finance_manager', 'agro']}>
            <InHouseWorkReportsList />
          </RoleProtectedRoute>
        } />
        <Route path="/crm/in-house-work-reports/:reportId" element={
          <RoleProtectedRoute allowedRoles={['owner', 'finance_manager', 'agro']}>
            <InHouseWorkReportDetail />
          </RoleProtectedRoute>
        } />

        {/* CRM Returns canonical path */}
        <Route path="/crm/returns" element={
          <ProtectedRoute>
            <ReturnsList />
          </ProtectedRoute>
        } />
        {/* Backward compatibility redirect */}
        <Route path="/returns" element={<Navigate to="/crm/returns" replace />} />

        {/* CRM Line Items canonical path */}
        <Route path="/crm/line-items" element={
          <ProtectedRoute>
            <LineItemsList />
          </ProtectedRoute>
        } />

        {/* CRM Employees canonical path */}
        <Route path="/crm/employees" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","hr_manager"]}>
            <EmployeesList />
          </RoleProtectedRoute>
        } />

        {/* Pontaj: the monthly timesheet of each firm */}
        <Route path="/crm/timesheets" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","hr_manager"]}>
            <TimesheetsPage />
          </RoleProtectedRoute>
        } />

        {/* Concedii: the leaves (periods) that write the pontaj's days */}
        <Route path="/crm/leaves" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","hr_manager"]}>
            <LeavesPage />
          </RoleProtectedRoute>
        } />

        {/* CRM Users canonical path */}
        <Route path="/crm/users" element={
          <RoleProtectedRoute allowedRoles={["owner","admin"]}>
            <UsersList />
          </RoleProtectedRoute>
        } />

        {/* CRM Employee Documents canonical path */}
        <Route path="/crm/employee-documents" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","hr_manager"]}>
            <EmployeeDocumentsList />
          </RoleProtectedRoute>
        } />

        <Route path="/crm/fleet" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","hr_manager","kran-admin","kran-operator"]}>
            <FleetList />
          </RoleProtectedRoute>
        } />


        <Route path="/crm/fleet/:id" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","hr_manager","kran-admin","kran-operator"]}>
            <FleetDetail />
          </RoleProtectedRoute>
        } />

        <Route path="/crm/fleet-documents" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","hr_manager","kran-admin","kran-operator"]}>
            <FleetDocumentsList />
          </RoleProtectedRoute>
        } />

        <Route path="/crm/fleet-maintenance" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","hr_manager","kran-admin","kran-operator"]}>
            <WorkOrdersList />
          </RoleProtectedRoute>
        } />

        <Route path="/crm/fleet-trip-sheets" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","hr_manager","kran-admin","kran-operator"]}>
            <TripSheets />
          </RoleProtectedRoute>
        } />

        <Route path="/crm/fleet-fuel" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","hr_manager","kran-admin","kran-operator"]}>
            <FuelLedger />
          </RoleProtectedRoute>
        } />

        {/* Agricultură (Agromixt): APIA crop diversification calculator. */}
        <Route path="/agricultura/arendatori" element={<RoleProtectedRoute allowedRoles={['agro']}><LeaseList /></RoleProtectedRoute>} />
        <Route path="/agricultura/arendatori/:id" element={<RoleProtectedRoute allowedRoles={['agro']}><LeaseDetail /></RoleProtectedRoute>} />
        <Route path="/agricultura/contracte" element={<RoleProtectedRoute allowedRoles={['agro']}><LeaseContractsPage /></RoleProtectedRoute>} />
        <Route path="/agricultura/plati" element={<RoleProtectedRoute allowedRoles={['agro']}><LeasePaymentsList /></RoleProtectedRoute>} />
        <Route path="/agricultura/parcele" element={<RoleProtectedRoute allowedRoles={['agro']}><LandParcelsList /></RoleProtectedRoute>} />
        <Route path="/agricultura/diversificare" element={
          <RoleProtectedRoute allowedRoles={['agro']}>
            <CropDiversificationPage />
          </RoleProtectedRoute>
        } />
        {/* Agricultură: parcel outline from ANCPI by carte funciară number (was on /tools). */}
        <Route path="/agricultura/cadastru" element={
          <RoleProtectedRoute allowedRoles={['agro']}>
            <CadastralLookupPage />
          </RoleProtectedRoute>
        } />
        <Route path="/agricultura" element={<Navigate to="/agricultura/diversificare" replace />} />

        <Route path="/parcele/view" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","hr_manager","kran-admin","agro"]}>
            <ParcelView />
          </RoleProtectedRoute>
        } />

        <Route path="/crm/inventory" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","call_center","orders","reception","field_agent","listing"]}>
            <InventoryJobsList />
          </RoleProtectedRoute>
        } />

        <Route path="/crm/inventory/:id" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","call_center","orders","reception","field_agent","listing"]}>
            <InventoryJobDetail />
          </RoleProtectedRoute>
        } />

        {/* CRM Line Item detail (component/model lookup) */}
        <Route path="/crm/line-items/detail" element={
          <ProtectedRoute>
            <LineItemDetail />
          </ProtectedRoute>
        } />

        <Route path="/crm/*" element={
          <ProtectedRoute>
            <CRM />
          </ProtectedRoute>
        } />

        <Route path="/dev-whatsapp-sync" element={
          <ProtectedRoute>
            <DevWhatsAppSync />
          </ProtectedRoute>
        } />

        <Route path="/whatsapp-inbox" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","call_center","orders","reception"]}>
            <WhatsAppInbox />
          </RoleProtectedRoute>
        } />
        <Route path="/whatsapp-inbox/:conversationId" element={
          <RoleProtectedRoute allowedRoles={["owner","admin","call_center","orders","reception"]}>
            <WhatsAppInbox />
          </RoleProtectedRoute>
        } />

        {isDevelopment && (
          <>
            <Route path="/design-system" element={
              <ProtectedRoute>
                <DesignSystem />
              </ProtectedRoute>
            } />
            <Route path="/dev-api-tester" element={
              <ProtectedRoute>
                <DevApiTester />
              </ProtectedRoute>
            } />
            <Route path="/line-items-test" element={
              <ProtectedRoute>
                <LineItemsTest />
              </ProtectedRoute>
            } />
            <Route path="/test-page" element={
              <ProtectedRoute>
                <TestPage />
              </ProtectedRoute>
            } />
          </>
        )}

        <Route path="/agent-sessions" element={
          <PermissionProtectedRoute requiredPermission="agent_dev_tools">
            <AgentSessions />
          </PermissionProtectedRoute>
        } />
        <Route path="/agent-sessions/:sessionId" element={
          <PermissionProtectedRoute requiredPermission="agent_dev_tools">
            <AgentSessions />
          </PermissionProtectedRoute>
        } />

        {/* Notifications - available in all environments */}
        <Route path="/notifications" element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        } />

        {/* CRM Return detail - available in all environments */}
        <Route path="/crm/returns/:id" element={
          <ProtectedRoute>
            <ReturnDetail />
          </ProtectedRoute>
        } />

        {/* CRM Tasks new canonical path */}
        <Route path="/crm/tasks" element={
          <ProtectedRoute>
            <CRMTasksList />
          </ProtectedRoute>
        } />
        {/* Backward compatibility redirect */}
        <Route path="/crmtasks" element={<Navigate to="/crm/tasks" replace />} />
        {/* Canonical CRM Tasks subroutes */}
        <Route path="/crm/tasks/picking" element={
          <ProtectedRoute>
            <Picking />
          </ProtectedRoute>
        } />
        <Route path="/crm/tasks/packing" element={
          <ProtectedRoute>
            <Packing />
          </ProtectedRoute>
        } />
        <Route path="/crm/tasks/printare" element={
          <ProtectedRoute>
            <Printare />
          </ProtectedRoute>
        } />
        {null}

        <Route path="/tools" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />

        <Route path="/account/settings" element={
          <ProtectedRoute>
            <UserSettings />
          </ProtectedRoute>
        } />

        <Route path="/tools/shipment-locations" element={
          <ProtectedRoute>
            <ShipmentLocations />
          </ProtectedRoute>
        } />

        <Route path="/tools/vehicle-models" element={
          <RoleProtectedRoute allowedRoles={['owner', 'admin']}>
            <VehicleModels />
          </RoleProtectedRoute>
        } />

        <Route path="/tools/vehicle-models/new" element={
          <RoleProtectedRoute allowedRoles={['owner', 'admin']}>
            <AddVehicleModel />
          </RoleProtectedRoute>
        } />

        <Route path="/tools/shipment-locations/unmapped" element={
          <ProtectedRoute>
            <ShipmentUnmappedCities />
          </ProtectedRoute>
        } />

        <Route path="/tools/components-shipping" element={
          <RoleProtectedRoute allowedRoles={["owner", "admin", "call_center"]}>
            <ComponentsShipping />
          </RoleProtectedRoute>
        } />

        <Route path="/tools/components-verification" element={
          <RoleProtectedRoute allowedRoles={["owner", "admin", "call_center"]}>
            <ComponentsVerification />
          </RoleProtectedRoute>
        } />

        <Route path="/tools/needs" element={
          <RoleProtectedRoute allowedRoles={['owner', 'admin']}>
            <AgentNeedsList />
          </RoleProtectedRoute>
        } />

        <Route path="/tools/penalitati" element={
          <ProtectedRoute>
            <PenaltyCalculator />
          </ProtectedRoute>
        } />

        {/* Backward compatibility redirects for old settings URLs */}
        <Route path="/settings" element={<Navigate to="/tools" replace />} />
        <Route path="/settings/shipment-locations" element={<Navigate to="/tools/shipment-locations" replace />} />
        <Route path="/settings/vehicle-models" element={<Navigate to="/tools/vehicle-models" replace />} />
        <Route path="/settings/vehicle-models/new" element={<Navigate to="/tools/vehicle-models/new" replace />} />
        <Route path="/settings/shipment-locations/unmapped" element={<Navigate to="/tools/shipment-locations/unmapped" replace />} />
        <Route path="/settings/components-shipping" element={<Navigate to="/tools/components-shipping" replace />} />

        <Route path="/kran-quotes" element={
          <PermissionProtectedRoute requiredPermission="kran_quotes">
            <KranQuotes />
          </PermissionProtectedRoute>
        } />
        <Route path="/kran-quotes/:id" element={
          <PermissionProtectedRoute requiredPermission="kran_quotes">
            <KranQuoteDetail />
          </PermissionProtectedRoute>
        } />

        <Route path="/client/:type" element={<ClientRequests />} />

        {/* Development Only Routes */}
        {isDevelopment && <Route path="/dev/*" element={<DevRoutes />} />}

        {/* Index Route - Redirect based on auth status */}
        <Route path="/" element={
          isAuthenticated 
            ? <Navigate to="/dashboard" replace />
            : <Navigate to="/login" replace />
        } />

        {/* 404 Route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <Router>
      <QueryProvider>
        <AuthProvider>
          <TooltipProvider>
            <RecordingPlayerProvider>
            <KioskProvider>
              <WhatsAppDrawerProvider>
                <DragDropProvider>
                  <AppRoutes />
                </DragDropProvider>
              </WhatsAppDrawerProvider>
            </KioskProvider>
            <RecordingPlayerCard />
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
              limit={3}
              style={{ zIndex: 100000 }}
            />
            </RecordingPlayerProvider>
          </TooltipProvider>
        </AuthProvider>
      </QueryProvider>
    </Router>
  );
}

export default App;
